import { inputName, type Keybinds } from '../input/keybinds';
import type { InputState } from '../input/inputState';
import { horizontalSpeed } from '../sim/player';
import type { Simulation } from '../sim/simulation';

export function fmtTime(t: number): string {
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return m + ':' + (s < 10 ? '0' : '') + s.toFixed(3);
}

const $ = (id: string) => document.getElementById(id)!;

/** Writes to the DOM only when a value changed. */
class Text {
  private v = '';
  constructor(private readonly el: HTMLElement) {}
  set(v: string) {
    if (v !== this.v) this.el.textContent = this.v = v;
  }
}

/** Text from a number: formats (allocates a string) only when the number changed. */
class NumText {
  private n = NaN;
  constructor(
    private readonly el: HTMLElement,
    private readonly fmt: (n: number) => string = String,
  ) {}
  set(n: number) {
    if (n !== this.n) this.el.textContent = this.fmt((this.n = n));
  }
}

class Cls {
  private v: string | null = null;
  constructor(private readonly el: HTMLElement) {}
  set(v: string) {
    if (v !== this.v) this.el.className = this.v = v;
  }
}

class Toggle {
  private v = false;
  constructor(private readonly el: HTMLElement) {}
  set(on: boolean) {
    if (on !== this.v) this.el.classList.toggle('on', (this.v = on));
  }
}

export class Hud {
  /** Timer in whole milliseconds. */
  private readonly timer = new NumText($('timer'), (ms) => fmtTime(ms / 1000));
  private readonly status = new NumText($('status'), (n) =>
    n === -1
      ? 'No timer zones'
      : n === -2
        ? 'Go to the start zone'
        : n === -3
          ? 'Start zone'
          : n === -4
            ? 'Finished'
            : 'Stage ' + Math.floor(n / 10000) + ' of ' + (n % 10000),
  );
  private readonly statusCls = new Cls($('status'));
  private readonly speed = new NumText($('speed'));
  private readonly speedCls = new Cls($('speed'));
  /** Seven fixed spans for the takeoff speeds; updated in place (no innerHTML on every jump). */
  private readonly jumpSpans: { el: HTMLSpanElement; text: Text; cls: Cls }[] = Array.from(
    { length: 7 },
    () => {
      const el = document.createElement('span');
      $('jumpRow').append(el);
      return { el, text: new Text(el), cls: new Cls(el) };
    },
  );
  private readonly stage = new NumText($('stStage'), (n) =>
    n < 0 ? '–' : Math.floor(n / 10000) + '/' + (n % 10000),
  );
  private readonly jumps = new NumText($('stJumps'));
  private readonly sync = new NumText($('stSync'), (n) => n + '%');
  /** PB in whole milliseconds, -1 for none. */
  private readonly pb = new NumText($('stPB'), (ms) => (ms < 0 ? 'none' : fmtTime(ms / 1000)));
  private readonly infoAA = new NumText($('infoAA'));
  private readonly infoAuto = new Text($('infoAuto'));
  private readonly keys = {
    w: new Toggle($('kW')),
    a: new Toggle($('kA')),
    s: new Toggle($('kS')),
    d: new Toggle($('kD')),
    duck: new Toggle($('kDuck')),
    jump: new Toggle($('kJump')),
    left: new Toggle($('kL')),
    right: new Toggle($('kR')),
  };
  private readonly finish = $('finish');
  private lastJumps = -1;
  private lastJumpLen = -1;

  /** Label the show-keys overlay with the first key bound to each movement action. */
  private toastTimer = 0;

  /** Short message under the timer (zone editor prompts). `ms` 0 keeps it until replaced. */
  toast(msg: string, ms = 3500) {
    const el = $('toast');
    clearTimeout(this.toastTimer);
    el.textContent = msg;
    el.hidden = !msg;
    if (msg && ms) this.toastTimer = window.setTimeout(() => (el.hidden = true), ms);
  }

  setMapName(name: string) {
    $('infoMap').textContent = name;
  }

  setKeyLabels(binds: Keybinds) {
    const label = (id: string, codes: string[]) => {
      const el = $(id);
      el.textContent = codes.length ? inputName(codes[0]) : '–';
      el.title = codes.map(inputName).join(', ');
    };
    label('kW', binds.forward);
    label('kA', binds.moveleft);
    label('kS', binds.back);
    label('kD', binds.moveright);
  }

  update(
    sim: Simulation,
    input: InputState,
    pb: number | null,
    aa: number,
    autobhop: boolean,
    turnDir: number,
  ) {
    const t = sim.timer;
    this.timer.set(Math.round(t.time * 1000));
    const zones = sim.level.startZone !== null;
    // Status as a number (no new string unless it changed): < 0 fixed texts, else stage and count.
    this.status.set(
      !zones
        ? -1
        : !sim.armed
          ? -2
          : t.state === 'zone'
            ? -3
            : t.state === 'run'
              ? sim.stage * 10000 + sim.level.stageCount
              : -4,
    );
    this.statusCls.set(t.state === 'run' ? 'run' : t.state === 'done' ? 'done' : '');

    const st = sim.stats;
    const hs = horizontalSpeed(sim.player);
    this.speed.set(Math.round(hs));
    const old = (st.speedHist.length ? st.speedHist.get(0) : 0) || hs;
    this.speedCls.set(hs > old + 0.5 ? 'up' : hs < old - 0.5 ? 'down' : '');

    const js = st.jumpSpeeds;
    if (st.jumps !== this.lastJumps || js.length !== this.lastJumpLen) {
      this.lastJumps = st.jumps;
      this.lastJumpLen = js.length;
      for (let i = 0; i < this.jumpSpans.length; i++) {
        const s = this.jumpSpans[i];
        if (i >= js.length) {
          s.text.set('');
          continue;
        }
        const v = js.get(i);
        s.text.set(String(v));
        s.cls.set(i === 0 ? '' : v > js.get(i - 1) ? 'up' : v < js.get(i - 1) ? 'down' : '');
      }
    }

    this.stage.set(sim.level.stageCount ? sim.stage * 10000 + sim.level.stageCount : -1);
    this.jumps.set(st.jumps);
    this.sync.set(st.syncPercent);
    this.pb.set(pb === null ? -1 : Math.round(pb * 1000));
    this.infoAA.set(aa);
    this.infoAuto.set(autobhop ? 'on' : 'off');

    const k = this.keys;
    k.w.set(input.held('forward'));
    k.a.set(input.held('moveleft'));
    k.s.set(input.held('back'));
    k.d.set(input.held('moveright'));
    k.duck.set(input.held('duck'));
    k.jump.set(input.held('jump'));
    k.left.set(turnDir > 0);
    k.right.set(turnDir < 0);
  }

  showFinish(title: string, time: number, sub: string) {
    $('finTitle').textContent = title;
    $('finTime').textContent = fmtTime(time);
    $('finSub').textContent = sub;
    this.finish.hidden = false;
  }

  hideFinish() {
    this.finish.hidden = true;
  }
}
